import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { PopoverContent } from '~/components/ui/popover';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/utils/cn';

import { CompareEnrichedValue, CompareFeedValue, type CompareValueRenderer } from './compare-cell';
import type { CompareColumn, CompareField } from './compare-grid.types';
import { StructuredValueEditor } from './compare-structured-editor';

/**
 * Serialises a scalar value into the text a `text`/`textarea` editor edits.
 * Structured (`json`) fields do not use this — they render a form editor instead.
 */
function serializeForEditor(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return JSON.stringify(value);
}

interface CompareCellViewerProps {
  column: CompareColumn;
  field: CompareField;
  /** Product the cell belongs to, shown as an eyebrow so the popover is self-identifying. */
  productTitle: string;
  renderValue: CompareValueRenderer;
  /** True when the renderer tints its own content; forwarded so the expanded value matches the cell. */
  selfTinted: boolean;
  /** Enables the inline editor. Omit for a read-only viewer. Receives the value in
   *  the attribute's own shape (parsed for `json` fields). Rejects when the save
   *  fails so the viewer stays open with the operator's draft. */
  onCommit?: (value: unknown) => Promise<void>;
  /** Clears any edit on this field, returning it to the model's output. */
  onReset?: () => void;
  /** Closes the viewer — called after a commit so editing ends where it started. */
  onClose: () => void;
  /** Automation hook: popover content id; header, editor and actions derive their ids from it. */
  testId?: string;
}

/**
 * The expanded view of a single cell, opened by double-clicking it (or pressing
 * Enter / F2) — the grid's answer to widening a column in Excel. Values render
 * unclamped here, which is the whole point: the grid stays uniformly short and
 * the full value is one gesture away.
 *
 * Shows both halves of the comparison, since the reason to open a cell is
 * usually to read what the feed had against what enrichment produced.
 *
 * When `onCommit` is supplied and the field is editable, the enriched half
 * becomes an input — the viewer is the editor, so there's one interaction to
 * learn rather than a second competing one inside the clamped cell.
 */
export function CompareCellViewer({
  column,
  field,
  productTitle,
  renderValue,
  selfTinted,
  onCommit,
  onReset,
  onClose,
  testId = 'compare-cell-viewer',
}: CompareCellViewerProps) {
  const { t } = useTranslation('content-enrichment');
  // Every field is editable once editing is enabled; only the shape of the editor
  // differs by content type. Without `onCommit` the viewer is a read-only expander.
  const isEditable = !!onCommit;
  // `json` fields render a form of per-property inputs (StructuredValueEditor);
  // `textarea` fields (long prose) render a multi-line box.
  const isStructured = column.editor === 'json';
  const isMultiline = column.editor === 'textarea';
  // Seeded once per mount. The grid keys this component by cell, so moving to a
  // different cell remounts it with a fresh draft — no re-seeding effect needed.
  // Structured fields keep the value in its own shape; scalars edit as text.
  const [draft, setDraft] = useState<unknown>(() =>
    isStructured ? field.enrichedValue : serializeForEditor(field.enrichedValue),
  );
  // Scoped to this cell's save: each commit persists on its own, so the spinner
  // and disabled state live here rather than in a batch save bar.
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditable && !isStructured) editorRef.current?.focus();
  }, [isEditable, isStructured]);

  /** Persists the draft (already in the attribute's own shape) and closes on success.
   *  A rejected save keeps the editor open so the operator's edits survive (the
   *  mutation's onError raises the toast). */
  const commit = () => {
    if (isSaving) return;
    if (!onCommit) {
      onClose();
      return;
    }
    // Structured drafts are already the payload; scalar drafts are trimmed strings.
    const payload: unknown = isStructured
      ? draft
      : typeof draft === 'string'
        ? draft.trim()
        : draft;
    void (async () => {
      setIsSaving(true);
      try {
        await onCommit(payload);
        onClose();
      } catch {
        // Kept open on failure — onError already surfaced the reason.
      } finally {
        setIsSaving(false);
      }
    })();
  };

  /** Enter commits a single-line edit; Cmd/Ctrl+Enter commits a multi-line one. Escape is left to Radix. */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    if (isMultiline && !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    commit();
  };

  /** Draft as text for the scalar editors — the structured branch never reads this. */
  const draftText = typeof draft === 'string' ? draft : '';

  return (
    <PopoverContent
      align="start"
      side="bottom"
      sideOffset={2}
      collisionPadding={12}
      data-testid={testId}
      className="max-h-[min(30rem,70vh)] w-[min(34rem,90vw)] overflow-y-auto p-0"
      aria-label={t('compareGrid.viewer.ariaLabel', { field: column.label })}
    >
      <header className="bg-muted/40 sticky top-0 z-10 flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <p
            className="text-muted-foreground truncate text-[11px] tracking-wide uppercase"
            data-testid={`${testId}-product-title`}
          >
            {productTitle}
          </p>
          <h4 className="text-foreground text-sm font-semibold" data-testid={`${testId}-title`}>
            {column.label}
          </h4>
        </div>
        <Badge
          variant="outline"
          className="text-muted-foreground shrink-0 text-[10px]"
          data-testid={`${testId}-status-badge`}
        >
          {t(field.isEdited ? 'compareGrid.marker.edited' : `compareGrid.diff.${field.status}`)}
        </Badge>
      </header>

      <div className="space-y-4 px-4 py-3">
        <section data-testid={`${testId}-feed`}>
          <p className="text-muted-foreground mb-1.5 text-[10px] font-semibold uppercase">
            {t('compareGrid.columns.feed')}
          </p>
          <CompareFeedValue
            value={field.feedValue}
            renderValue={renderValue}
            fieldKey={field.key}
          />
        </section>

        <section data-testid={`${testId}-enriched`}>
          <p className="text-primary mb-1.5 text-[10px] font-semibold uppercase">
            {t('compareGrid.columns.enriched')}
          </p>

          {isEditable ? (
            <div className="space-y-2">
              {isStructured ? (
                // A form of per-property inputs — no raw JSON. The draft is kept in
                // the value's own shape and is ready to persist as-is.
                <StructuredValueEditor
                  value={draft}
                  onChange={setDraft}
                  disabled={isSaving}
                  testId={`${testId}-editor`}
                />
              ) : isMultiline ? (
                <Textarea
                  ref={editorRef as React.Ref<HTMLTextAreaElement>}
                  value={draftText}
                  disabled={isSaving}
                  onChange={(e) => {
                    setDraft(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  rows={5}
                  data-testid={`${testId}-textarea`}
                  aria-label={column.label}
                  className="text-sm"
                />
              ) : (
                <Input
                  ref={editorRef as React.Ref<HTMLInputElement>}
                  value={draftText}
                  disabled={isSaving}
                  onChange={(e) => {
                    setDraft(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  data-testid={`${testId}-input`}
                  aria-label={column.label}
                  className="text-sm"
                />
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground text-[11px]">
                  {isStructured
                    ? null
                    : t(
                        isMultiline
                          ? 'compareGrid.editor.commitHintMultiline'
                          : 'compareGrid.editor.commitHint',
                      )}
                </span>
                <span className="flex items-center gap-2">
                  {field.isEdited && onReset && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isSaving}
                      data-testid={`${testId}-revert-button`}
                      className={cn(isSaving ? 'cursor-not-allowed' : 'cursor-pointer')}
                      onClick={() => {
                        onReset();
                        onClose();
                      }}
                    >
                      {t('compareGrid.editor.revert')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    isLoading={isSaving}
                    disabled={isSaving}
                    data-testid={`${testId}-apply-button`}
                    className={cn(isSaving ? 'cursor-not-allowed' : 'cursor-pointer')}
                    onClick={commit}
                  >
                    {t('compareGrid.editor.apply')}
                  </Button>
                </span>
              </div>
            </div>
          ) : (
            <CompareEnrichedValue field={field} renderValue={renderValue} selfTinted={selfTinted} />
          )}
        </section>
      </div>

      <footer className="text-muted-foreground border-t px-4 py-2 text-[11px]">
        {t('compareGrid.viewer.dismissHint')}
      </footer>
    </PopoverContent>
  );
}
